import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    FormControl,
    Select,
    Grid,
    InputAdornment,
    OutlinedInput,
    Link,
    Stack,
    InputLabel
} from '@mui/material';
import {
    Pencil,
    RefreshCcw,
    Search,
    Filter,
    Eye,
    X
} from 'lucide-react';
import { getEnquiries, getEnquiryById, updateEnquiry } from '../../Services/ContentHub/ContactusApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import GlobalPagination from '../../Components/Common/Globalpagination';

const statusBadgeSx = (status) => {
    switch (status?.toLowerCase()) {
        case 'resolved':
            return { bgcolor: '#E8F5E8', color: '#2E7D32' };
        case 'read':
            return { bgcolor: '#EFF6FF', color: '#1D4ED8' };
        case 'unread':
        case 'pending':
            return { bgcolor: '#FFF8E1', color: '#B45309' };
        default:
            return { bgcolor: '#F1F5F9', color: '#475569' };
    }
};

const Enquiries = () => {
    const { showSnackbar } = useGlobalSnackbar();

    // List & Loading State
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination State
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter State
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusCounts, setStatusCounts] = useState({
        read: 0,
        unread: 0,
        resolved: 0
    });

    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [updateForm, setUpdateForm] = useState({
        status: '',
        remarks: ''
    });
    const [updating, setUpdating] = useState(false);

    // View Details Modal State
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [viewEnquiry, setViewEnquiry] = useState(null);

    // Fetch enquiries
    const fetchEnquiries = useCallback(async (signal) => {
        try {
            setLoading(true);
            const params = {
                page,
            };
            if (rowsPerPage !== 10) params.pageSize = rowsPerPage;
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter;

            const response = await getEnquiries(params);
            if (response && response.success) {
                setEnquiries(response.data || []);
                setTotalRecords(response.totalRecords || 0);
                if (response.statusCounts) {
                    setStatusCounts(response.statusCounts);
                }
            } else {
                setError("Failed to load enquiries.");
            }
        } catch (err) {
            if (!signal?.aborted && err.name !== 'CanceledError') {
                setError("Failed to fetch enquiries. Please try again later.");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [page, rowsPerPage, search, statusFilter]);

    useEffect(() => {
        const controller = new AbortController();
        fetchEnquiries(controller.signal);
        return () => controller.abort();
    }, [fetchEnquiries]);

    // Handlers
    const handleOpenViewDialog = async (enquiry) => {
        setViewEnquiry(enquiry);
        setOpenViewDialog(true);

        try {
            const response = await getEnquiryById(enquiry.id);
            if (response && response.success && response.data) {
                const updatedData = response.data;
                setViewEnquiry(updatedData);

                // Update row in table list
                setEnquiries(prevList =>
                    prevList.map(item => item.id === enquiry.id ? updatedData : item)
                );

                // If status was unread or pending, reload list & counts
                if (enquiry.status === 'unread' || enquiry.status === 'pending') {
                    fetchEnquiries();
                }
            }
        } catch (err) {
            console.error("Failed to fetch enquiry details by ID", err);
        }
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setViewEnquiry(null);
    };

    const handleOpenDialog = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setUpdateForm({
            status: enquiry.status || 'pending',
            remarks: enquiry.remarks || ''
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedEnquiry(null);
        setUpdateForm({ status: '', remarks: '' });
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateSubmit = async () => {
        if (!selectedEnquiry) return;

        setUpdating(true);
        try {
            const response = await updateEnquiry(selectedEnquiry.id, updateForm);
            if (response && response.success) {
                showSnackbar("Enquiry updated successfully.", "success");
                handleCloseDialog();
                fetchEnquiries();
            } else {
                showSnackbar(response.message || "Failed to update enquiry.", "error");
            }
        } catch (err) {
            console.error("Update error:", err);
            const errMsg = err.response?.data?.message || err.message || "An error occurred while updating.";
            showSnackbar(errMsg, "error");
        } finally {
            setUpdating(false);
        }
    };



    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header & Filters Section */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                            Job & Other Enquiries
                        </Typography>
                        <Button
                            disableElevation disableFocusRipple disableRipple
                            startIcon={<RefreshCcw size={16} />}
                            onClick={() => fetchEnquiries()}
                            variant="outlined"
                            sx={{
                                textTransform: 'none',
                                borderRadius: '6px',
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                px: 2,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <OutlinedInput
                                fullWidth
                                size="small"
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                        <Search size={16} color="#64748B" />
                                    </InputAdornment>
                                }
                                sx={{
                                    borderRadius: '6px',
                                    bgcolor: '#FFFFFF',
                                    height: '38px',
                                    fontSize: '0.8rem',
                                    '& fieldset': { borderColor: '#E2E8F0' },
                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setPage(1);
                                    }}
                                    displayEmpty
                                    startAdornment={
                                        <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                            <Filter size={15} color="#64748B" style={{ marginLeft: '4px' }} />
                                        </InputAdornment>
                                    }
                                    sx={{
                                        borderRadius: '6px',
                                        bgcolor: '#FFFFFF',
                                        height: '38px',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }}
                                >
                                    <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Status</MenuItem>
                                    <MenuItem value="read" sx={{ fontSize: '0.8rem' }}>Read</MenuItem>
                                    <MenuItem value="unread" sx={{ fontSize: '0.8rem' }}>Unread</MenuItem>
                                    <MenuItem value="resolved" sx={{ fontSize: '0.8rem' }}>Resolved</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>

                {/* Status Summary Cards */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2, flexWrap: 'wrap' }}>
                    {[
                        { label: 'All Enquiries', value: totalRecords || 0, status: 'all', color: '#64748B', activeBg: '#F8FAFC', activeBorder: '#64748B' },
                        { label: 'Read', value: statusCounts.read || 0, status: 'read', color: '#1D4ED8', activeBg: '#EFF6FF', activeBorder: '#3B82F6' },
                        { label: 'Unread', value: statusCounts.unread || 0, status: 'unread', color: '#B45309', activeBg: '#FFFBEB', activeBorder: '#F59E0B' },
                        { label: 'Resolved', value: statusCounts.resolved || 0, status: 'resolved', color: '#15803D', activeBg: '#F0FDF4', activeBorder: '#22C55E' }
                    ].map((card) => {
                        const isActive = statusFilter === card.status;
                        return (
                            <Paper
                                key={card.label}
                                elevation={0}
                                onClick={() => {
                                    setStatusFilter(card.status);
                                    setPage(1);
                                }}
                                sx={{
                                    flex: '1 1 120px',
                                    minWidth: '120px',
                                    p: 1.5,
                                    borderRadius: '6px',
                                    bgcolor: isActive ? card.activeBg : '#FFFFFF',
                                    border: isActive ? `1px solid ${card.activeBorder}` : '1px solid #E2E8F0',
                                    cursor: 'pointer',
                                    boxShadow: 'none',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: card.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {card.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#0F172A', mt: 0.5 }}>
                                    {card.value}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>

                <Paper sx={{ borderRadius: "6px", border: "1px solid #E2E8F0", overflow: "hidden", bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1250, tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Lead Info</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Type</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Message</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Admin Remarks</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                            <Typography color="error" sx={{ fontSize: '0.8rem' }}>{error}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : enquiries.length > 0 ? (
                                    enquiries.map((enquiry, idx) => (
                                        <TableRow key={enquiry.id} sx={{
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell align="left" sx={{ color: '#64748B', fontSize: '0.75rem', py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                {page * rowsPerPage + idx + 1}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 200 }}>
                                                <Typography sx={{ fontWeight: 500, color: '#1E293B', fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {enquiry.name}
                                                </Typography>
                                                <Typography sx={{ color: '#64748B', display: 'block', fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {enquiry.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1, py: 0.25,
                                                    bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.7rem',
                                                    fontWeight: 500, borderRadius: '4px'
                                                }}>
                                                    {enquiry.retreat_type || 'General'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 220 }}>
                                                <Tooltip title={enquiry.message || 'No message'} arrow placement="top">
                                                    <Typography sx={{
                                                        color: '#334155', fontSize: '0.75rem',
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                    }}>
                                                        {enquiry.message || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.25, py: 0.35,
                                                    borderRadius: '4px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize',
                                                    ...statusBadgeSx(enquiry.status)
                                                }}>
                                                    {enquiry.status || 'Pending'}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, maxWidth: 180 }}>
                                                {enquiry.remarks ? (
                                                    <Tooltip title={enquiry.remarks} arrow placement="top">
                                                        <Typography sx={{
                                                            color: '#475569', fontSize: '0.75rem',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                        }}>
                                                            {enquiry.remarks}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography sx={{ color: '#94A3B8', fontSize: '0.75rem' }}>
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748B' }}>
                                                {enquiry.cuName || enquiry.name || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748B' }}>
                                                {enquiry.uuName || enquiry.adminUser?.name || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748B' }}>
                                                {formatDate(enquiry.createdAt)}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748B' }}>
                                                {formatDate(enquiry.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            disableFocusRipple disableRipple disableTouchRipple
                                                            onClick={() => handleOpenViewDialog(enquiry)}
                                                            sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                                                        >
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Update Status">
                                                            <IconButton
                                                                size="small"
                                                                disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleOpenDialog(enquiry)}
                                                                sx={{ color: '#FFBD65', p: 0.5 }}
                                                            >
                                                                <Pencil size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No enquiries found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <GlobalPagination
                        page={page}
                        pageSize={rowsPerPage}
                        totalRecords={totalRecords}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newSize) => {
                            setRowsPerPage(newSize);
                            setPage(1);
                        }}
                        loading={loading}
                    />
                </Paper>

                {/* UPDATE STATUS MODAL */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="xs"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF',
                            maxWidth: 440
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                            Update Enquiry Status
                        </Typography>
                        <IconButton
                            size="small"
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseDialog}
                            sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 1.5, mb: 2 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Enquiry from:</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B', mt: 0.25 }}>
                                {selectedEnquiry?.name} ({selectedEnquiry?.email || 'No email'})
                            </Typography>
                        </Box>

                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontSize: '0.8rem', color: '#64748B', '&.Mui-focused': { color: '#FFBD65' } }}>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={updateForm.status}
                                    label="Status"
                                    onChange={handleUpdateChange}
                                    sx={{
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }}
                                >
                                    <MenuItem value="read" sx={{ fontSize: '0.8rem' }}>Read</MenuItem>
                                    <MenuItem value="unread" sx={{ fontSize: '0.8rem' }}>Unread</MenuItem>
                                    <MenuItem value="resolved" sx={{ fontSize: '0.8rem' }}>Resolved</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                name="remarks"
                                label="Internal Admin Remarks"
                                value={updateForm.remarks}
                                onChange={handleUpdateChange}
                                multiline
                                rows={3}
                                placeholder="Add internal follow-up notes..."
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: '#E2E8F0' },
                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    },
                                    '& .MuiInputLabel-root': { fontSize: '0.8rem', color: '#64748B' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                }}
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleCloseDialog}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleUpdateSubmit}
                            variant="contained"
                            disabled={updating}
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#000000',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                px: 3,
                                py: 0.75
                            }}
                        >
                            {updating ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* VIEW DETAILS MODAL */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF',
                            maxWidth: 540
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: '#1E293B' }}>
                            Enquiry Details #{viewEnquiry?.id}
                        </Typography>
                        <IconButton
                            size="small"
                            disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleCloseViewDialog}
                            sx={{ color: '#64748B', p: 0.5, '&:hover': { color: '#1E293B' } }}
                        >
                            <X size={16} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        {viewEnquiry && (
                            <Stack spacing={2}>
                                {/* Overview Card */}
                                <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>
                                            Lead Name
                                        </Typography>
                                        <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: '#1E293B' }}>
                                            {viewEnquiry.name || '—'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.25 }}>
                                            Retreat Type: <strong style={{ color: '#1E293B' }}>{viewEnquiry.retreat_type || 'General'}</strong>
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        display: 'inline-block',
                                        px: 1.25, py: 0.35, borderRadius: '4px',
                                        fontSize: '0.7rem', fontWeight: 500,
                                        textTransform: 'capitalize',
                                        ...statusBadgeSx(viewEnquiry.status)
                                    }}>
                                        {viewEnquiry.status || 'Pending'}
                                    </Box>
                                </Box>

                                {/* Contact Table Card */}
                                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid #F1F5F9' }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Email Address</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#1976D2', fontWeight: 500 }}>
                                            {viewEnquiry.email ? (
                                                <Link href={`mailto:${viewEnquiry.email}`} underline="hover" color="inherit">
                                                    {viewEnquiry.email}
                                                </Link>
                                            ) : '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25 }}>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Mobile Number</Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#1E293B', fontWeight: 500 }}>
                                            {viewEnquiry.mobile ? (
                                                <Link href={`tel:${viewEnquiry.mobile}`} underline="hover" color="inherit">
                                                    {viewEnquiry.mobile}
                                                </Link>
                                            ) : '—'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Message */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                        Message
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', p: 1.5, borderRadius: '6px' }}>
                                        <Typography sx={{ fontSize: '0.8rem', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                            {viewEnquiry.message || 'No message provided.'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Admin Remarks */}
                                {viewEnquiry.remarks && (
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                                            Internal Admin Remarks
                                        </Typography>
                                        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', p: 1.5, borderRadius: '6px' }}>
                                            <Typography sx={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                                {viewEnquiry.remarks}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* Timestamps */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                                        Submitted: {formatDate(viewEnquiry.createdAt)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                                        Updated by: <strong style={{ color: '#475569' }}>{viewEnquiry.adminUser?.name || 'Admin'}</strong>
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleCloseViewDialog}
                            sx={{
                                borderColor: '#E2E8F0',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                color: '#475569',
                                textTransform: 'none',
                                px: 2.5,
                                py: 0.75,
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation disableFocusRipple disableRipple
                            onClick={() => {
                                handleCloseViewDialog();
                                handleOpenDialog(viewEnquiry);
                            }}
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#000000',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: 2.5,
                                py: 0.75
                            }}
                        >
                            Update Status
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default Enquiries;
